import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SCHEMAS_ANALYTICS_ACTIONS} from 'constants/analytics-products/SchemasAnalyticsConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import {BatchLinkProps, BatchSchema} from 'constants/SampleConstants';
import {
  FieldDataMigrationStatus,
  TagSchemaListingSearchInput,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readQuerySafe, writeQuery} from 'graphql-client/ApolloWrappers';
import BenchlingQuery, {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {
  FieldTableSchemaQuery,
  FieldTableSchemaQuery_schema as FieldTableSchemaQuerySchema,
  FieldTableSchemaQueryVariables,
} from 'registry/field/__generated/FieldTableSchemaQuery';
import {DisablableControl, FieldMigrationReviewProps} from 'registry/field/FieldTableRow';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {FieldMigrationReviewModal} from 'schemas/fields/field-migration/FieldMigrationReviewModal';
import PopulateFromAnotherFieldModal, {
  FieldToFieldMigrationSettings,
} from 'schemas/fields/field-migration/PopulateFromAnotherFieldModal';
import * as SchemaFieldMigrationDAO from 'schemas/fields/field-migration/SchemaFieldMigrationDAO';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {SchemaFieldNameCallbackData} from 'schemas/fields/SchemaFieldConstants';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {shouldShowWarehouseName} from 'schemas/fields/SchemaFieldWarehouseNameField';
import RelatedBenchlingAppsArchiveWarning from 'schemas/RelatedBenchlingAppsArchiveWarning';
import {Schema} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {convertSnapshotField, ConvertSnapshotFieldResponse} from 'schemas/snapshot-field/SnapshotFieldDAO';
import {TagSchemaFileSettingsQuery} from 'settings/registry/__generated/TagSchemaFileSettingsQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import ReorderableSimpleTableRow, {
  ReorderableSimpleTableRowProps,
} from 'ui-library/reorderable/ReorderableSimpleTableRow';
import runModal from 'ui-library/runModal';
import {confirm} from 'util/Modals';
import {capitalize} from 'util/NameUtil';
import {omit} from 'util/omitKey';
import {withLoadingScreen} from 'util/Promises';
import {MakeFieldsOptional} from 'util/Types';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {
  FieldTableQuery,
  FieldTableQuery_schemaFieldSelectors_SchemaFieldSelectorNode as FieldTableQuerySelector,
  FieldTableQueryVariables,
  FieldTableQuery_tagSchemasListing_TagSchemaListingNode_tagSchemas_TagSchemaNode as TagSchemaGQL,
} from './__generated/FieldTableQuery';
import {CONFIRM_DELETE_ALL} from './FieldTableConstants';
import FieldTableHeader from './FieldTableHeader';
import {shouldEnableSchemaLinkForField} from './FieldTableUtil';

type ActiveMigration = ExtractByTypename<
  TagSchemaFileSettingsQuery['tagSchema'],
  'TagSchemaNode'
>['activeMigration'];

type SchemaInterface = FieldTableQuery['schemaInterfaces'][number];

const {SAVING_STATES} = RegistryConstants;

const SHOW_ARCHIVED_ROW_ID = 'special-show-archived-fields-row';

export function createClientSchemaField(fieldName: string, systemName?: string): ClientSchemaField {
  const newCid = _.uniqueId();

  return {
    archiveRecord: null,
    cid: newCid,
    name: fieldName,
    displayName: fieldName,
    systemName: systemName ?? '',
    fieldType: SchemaFieldConstants.FIELD_TYPES.TEXT.type,
    isRequired: false,
    isComputed: false,
    isConvertedFromLink: false,
    isSnapshot: false,
    isIntegration: false,
    isMulti: false,
    isParentLink: false,
    description: null,
    tooltipText: null,
  };
}

function mapPropsToState(props: FieldTableProps): State {
  // `editingFieldCids` tracks fields that are currently being edited so that the editing state is
  // properly maintained when other operations are exectued (e.g. moving a row)
  // `fields` is necessary for maintaining the "unsaved" field states (e.g. creating a new field that
  // has not yet been saved via `onFieldsChange`)
  return {
    editingFieldCids: [],
    fields: props.fields.map((field) => ({...field, cid: _.uniqueId()})),
    isShowingArchived: false,
  };
}

function replaceFieldsFromProps(props: FieldTableProps, state: State): State['fields'] {
  const existingFieldsById = _.indexBy(state.fields, 'id');

  const newFields = props.fields.map((field) => {
    const fieldId = field.id;
    const existingFieldInState = fieldId ? existingFieldsById[fieldId] : undefined;
    const cid = existingFieldInState?.cid ?? _.uniqueId();
    return {
      cid,
      ...field,
    };
  });

  return newFields;
}

export type ClientSchemaField = MakeFieldsOptional<
  SchemaFieldConstants.SchemaField,
  'id' | 'dbId' | 'apiId' | 'requiredLink'
> & {
  cid: string;
};

export function isClientSchemaField(
  field: SchemaFieldConstants.SchemaField | ClientSchemaField
): field is ClientSchemaField {
  return field.hasOwnProperty('cid');
}

interface BaseProps {
  assaySchemas?: Array<AssaySchema> | null;
  batchLinkProps?: BatchLinkProps;
  batchSchemas?: Array<BatchSchema> | null;
  fileType?: string;
  itemType?: TagSchemaItemType | null;
  ownerId: string;
  ownerUrlId: string;
  registryId?: string;
  schema?: Schema | null;
  schemaFieldDependentsSummariesById?: {[id: string]: Array<String>}; // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaHasData?: boolean; // Have any items of this schema been created?
  schemaNumItems?: number;
  schemaId: string | null;
  sequenceType?: RegistryConstants.SequenceSchemaType | null;
  shouldShowIsIntegration?: boolean;
  shouldShowIsParentLink?: boolean;
  shouldShowIsRequired?: boolean;
  shouldShowMultiSelect?: boolean;
  shouldShowValidations?: boolean;
  shouldShowTooltipText?: boolean;
  storableSchemas?: Array<StorableSchema>;
  submissionWarnings?: Array<JSX.Element>;
  archiveWarnings?: Array<JSX.Element>;
  activeMigration?: ActiveMigration;
  compactMode?: boolean;
}

export interface ItemProps extends BaseProps {
  disabled: boolean;
  id: string;
  initialField: ClientSchemaField; // TODO(BNCH-30720) Does not match FieldTableRowProps
  isInEditMode: boolean;
  isFieldBeingCreated: boolean;
  isSchemaOrInterfaceBeingCreated: boolean;
  onCancel: (fieldCid: string) => void;
  onEdit: (fieldCid: string) => void;
  requestArchive: (fieldCid: string) => void;
  requestChange: (fieldCid: string, newFieldData: Partial<ClientSchemaField>) => void;
  requestRemove: (fieldCid: string) => void;
  requestUnarchive: (fieldCid: string) => void;
  isFieldMigrationAvailable: boolean;
  refetchSchemaAfterUpdate?: () => Promise<void>;
  schemaFieldSelectors: QueriedProps['schemaFieldSelectors'];
  fieldMigrationReviewProps?: FieldMigrationReviewProps;
  tagSchemas?: Array<TagSchemaSummaryGraphQL>;
}

export enum SaveAction {
  CHANGE,
  ARCHIVE,
  UNARCHIVE,
  REMOVE,
}

export interface FieldNameRenderingProps {
  onFieldNameClick?: (field: SchemaFieldNameCallbackData) => void;
  fieldNameTooltip?: string;
}

export interface BaseFieldTableProps extends BaseProps {
  fields: Array<ClientSchemaField | SchemaFieldConstants.SchemaField>;
  isBeingCreated?: boolean;
  isDisabled: boolean;
  // React component class of the list items to render. `ReorderableList` will render
  //   <itemClass {...itemProps} isDragging={...} onDragStart={...} />
  // for each `itemProps` in `itemsProps`.
  // TODO(TypeScript): Validate against ItemProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemClass: React.ComponentType<any>;
  // onFieldsChange is called with (fields, {deleteOldSchemaTags, renameOldSchemaTags} = {})
  onFieldsChange?: (
    newFields: Array<ClientSchemaField>,
    args?: {deleteOldSchemaTags?: boolean; renameOldSchemaTags?: boolean}
  ) => void;
  refetchSchemaAfterUpdate?: () => Promise<void>;
  /**
   * During creation, fields are persisted with the schema overall, but during editing,
   * a user can add, remove, or update individual fields. Provide a way to listen to
   * which explicit user action was taken rather than receiving the overall state of
   * fields in the `onFieldsChange` hook. This is only called during editing.
   */
  onFieldSave?: (field: ClientSchemaField, saveAction: SaveAction) => void;
  savingState: RegistryConstants.SavingState;
  schemaType: string;
  nonSchemaNameOverride?: string;
  handleDeleteConfirmation?: (onSubmit: (param?: string) => void) => void;
  subheader?: JSX.Element;
  dataTestAttrs?: DataTestAttrs;
  isFieldMigrationEnabled?: boolean;
  verifyFields?: (fields: Array<ClientSchemaField>, schemaId?: string | null) => boolean;
  tagSchemasFilter?: (tagSchema: TagSchemaGQL, props: BaseProps) => boolean;
  enforceUniqueDisplayName?: boolean;
  title?: string;
  titleTooltip?: string;
  /**
   * If true, the component will use props.fields as the source of truth.
   * CIDs from the props will be preserved.
   * This is useful for when the parent component wants to update the fields.
   */
  shouldReplaceFieldsFromProps?: boolean;
  // If provided, customizes the rendering of field names (click handler and tooltip)
  fieldNameRenderingProps?: FieldNameRenderingProps;
}

interface QueriedProps {
  schemaInterfaces: Array<SchemaInterface>;
  schemaFieldSelectors: Array<FieldTableQuerySelector> | Array<SchemaFieldSelector>;
  tagSchemas?: Array<TagSchemaSummaryGraphQL>;
}

export type FieldTableProps = BaseFieldTableProps & QueriedProps;

interface State {
  editingFieldCids: Array<string>;
  fields: Array<ClientSchemaField>;
  isShowingArchived: boolean;
}

export class FieldTable extends React.Component<FieldTableProps, State> {
  static defaultProps = {
    isBeingCreated: false,
    isDisabled: false,
    shouldShowIsParentLink: false,
    shouldShowIsRequired: false,
    shouldShowMultiSelect: true,
    shouldShowValidations: false,
    shouldShowIsIntegration: false,
    isFieldMigrationEnabled: false,
    tagSchemas: [],
    batchSchemas: [],
    containerSchemas: [],
    plateSchemas: [],
    assaySchemas: [],
    submissionWarnings: [],
    archiveWarnings: [],
    enforceUniqueDisplayName: true,
    shouldReplaceFieldsFromProps: false,
  };

  override state: State = mapPropsToState(this.props);

  override componentDidMount(): void {
    if (this.props.savingState !== SAVING_STATES.PENDING && !LoadingScreen.isHidden()) {
      LoadingScreen.hide();
    }
  }

  private getIdOfNewField(nextProps: FieldTableProps): number | null {
    const newIds = _.compact(
      _.difference(
        nextProps.fields.map((field) => field.id),
        this.props.fields.map((field) => field.id)
      )
    );
    if (newIds.length === 0) {
      return null;
    }
    assert(newIds.length === 1, 'Found more than one ID in new FieldTable props.');
    return newIds[0];
  }

  override UNSAFE_componentWillReceiveProps(nextProps: FieldTableProps): void {
    if (nextProps.schemaId !== this.props.schemaId) {
      // Special case 1: Reset state when navigating to a different TagSchema
      this.setState(mapPropsToState(nextProps));
      return;
    }

    if (
      (this.props.savingState === SAVING_STATES.FAILURE &&
        nextProps.savingState === SAVING_STATES.COMPLETED) ||
      nextProps.savingState === SAVING_STATES.FAILURE
    ) {
      // Special case 2: Revert fields to original values if not successfully updated
      this.setState(mapPropsToState(this.props));
      return;
    }

    if (this.props.isBeingCreated) {
      // Special case 3: When creating the schema:
      if (nextProps.shouldReplaceFieldsFromProps) {
        // case A: this component is a controlled component.
        // We update the state with the new fields. This is particularly useful for when fieldsets
        // are added to the schema and the fieldset controlled fields need to be created.
        // Unfortunately, this is dangerous to do everywhere since often times the field table rows get
        // re-mounted causing focus to be drawn away when updating unrelated attributes.
        this.setState({fields: replaceFieldsFromProps(nextProps, this.state)});
        return;
      } else {
        // case B: this component hopes that the state and props are in sync.
        // This is true because only the field table can edit these fields.
        // Thus, do nothing.
        return;
      }
    }

    if (nextProps.shouldReplaceFieldsFromProps) {
      // Special case 4: Parent component wants to update the fields.
      // When schema <-> interface auto-conformity is enabled, it is possible for the interface to add more than one field to an existing schema.
      // We need to bypass the check for receiving more than one new field (getIdOfNewField) in this case.
      // To do so, we present a new contract for the FieldTable: If props are updated, we assume that it is OK
      // to wipe out the current state and accept the new props as the source of truth, but with CIDs preserved.
      this.setState({fields: replaceFieldsFromProps(nextProps, this.state)});
      return;
    }

    // Otherwise, incorporate any server updates into state.fields
    const idOfNewField = this.getIdOfNewField(nextProps);
    let updatedFields = this.state.fields.map((currentField) => {
      // Update with new field information from the server, but also keep our own information like the cid
      let matchingFieldFromServer;
      if (currentField.id) {
        matchingFieldFromServer = nextProps.fields.find((serverField) => serverField.id === currentField.id);
      } else {
        matchingFieldFromServer = nextProps.fields.find((serverField) => serverField.id === idOfNewField);
      }
      return {...currentField, ...matchingFieldFromServer};
    });
    // In the case that we deleted a persisted field, make sure we also remove it from the state.  Without
    // this filter other calls to update the state may be overridden by this update because setState can
    // batch updates.
    updatedFields = updatedFields.filter(
      (currentField) =>
        currentField.dbId == null ||
        nextProps.fields.find((serverField) => serverField.id === currentField.id)
    );
    this.setState({fields: updatedFields});
  }

  override componentDidUpdate(prevProps: FieldTableProps, prevState: State): void {
    // Show `LoadingScreen` so
    //   - user doesn't try changing more fields while the current fields are saving
    //   - user knows saving is occurring: schema saving takes a while because it has to sync all of the
    //     items with that schema
    const wasSaving = prevProps.savingState === SAVING_STATES.PENDING;
    const isSaving = this.props.savingState === SAVING_STATES.PENDING;
    if (!wasSaving && isSaving) {
      // Saving started
      LoadingScreen.show('Saving...');
    } else if (wasSaving && !isSaving) {
      // Saving finished
      LoadingScreen.hide();
    }
  }

  private verifyFields(fields: Array<ClientSchemaField>): boolean {
    // TODO(T6639): If verification fails, field values should revert to original values
    for (const field of fields) {
      const duplicateDisplayName =
        this.props.enforceUniqueDisplayName &&
        fields.filter((field2) => field.name === field2.name).length > 1;
      const duplicateSystemName =
        fields.filter((field2) => field.systemName === field2.systemName).length > 1;
      if (duplicateDisplayName || duplicateSystemName) {
        notifications.error(
          `Field ${field.name} (${field.systemName}) cannot have the same display name or system name as another field in ${this.props.schemaType} schema. Please note that this includes archived fields.`
        );
        return false;
      }
    }
    if (this.props.verifyFields && !this.props.verifyFields(fields, this.props.schemaId)) {
      return false;
    }

    return true;
  }

  private shouldDisableAddField(): boolean {
    return (
      this.props.isDisabled ||
      (!this.props.isBeingCreated && this.state.fields.filter((field) => field.id == null).length > 0)
    );
  }

  private handleAddField = (): void => {
    if (this.shouldDisableAddField()) {
      // Since the server is not aware of the cid/id mapping, the component must assume that the new id from
      // the server belongs to the row which does not yet have an id, and update the state accordingly.
      // To this end, we can only support a single cid without an id in flight at a time. T7148 will change
      // this component to only allow editing all fields at once, making this stipulation no longer relevant.
      return;
    }
    const newField = createClientSchemaField('');
    const newFields = reactUpdate(this.state.fields, {$push: [newField]});
    this.setState({
      editingFieldCids: reactUpdate(this.state.editingFieldCids, {$push: [newField.cid]}),
      fields: newFields,
    });
    if (this.props.isBeingCreated && this.props.onFieldsChange) {
      this.props.onFieldsChange(newFields);
    }
  };

  private handleCancelEditField(fieldCid: string): void {
    const editingFieldCidIndex = this.state.editingFieldCids.findIndex((cid) => cid === fieldCid);
    const newEditingFieldCids =
      editingFieldCidIndex > -1
        ? reactUpdate(this.state.editingFieldCids, {
            $splice: [[editingFieldCidIndex, 1]],
          })
        : this.state.editingFieldCids;
    this.setState({editingFieldCids: newEditingFieldCids});
  }

  private handleEditField(fieldCid: string): void {
    this.setState({
      editingFieldCids: reactUpdate(this.state.editingFieldCids, {$push: [fieldCid]}),
    });
  }

  private handleChangeField(fieldCid: string, newFieldData: Partial<ClientSchemaField>): void {
    // @ts-expect-error
    const oldField: ClientSchemaField = this.state.fields.find((f) => f.cid === fieldCid);
    const newField: ClientSchemaField = {...oldField, ...newFieldData};

    this.handleReplaceField(fieldCid, newField);

    if (this.props.onFieldSave && !this.props.isBeingCreated) {
      this.props.onFieldSave(newField, SaveAction.CHANGE);
    }
  }

  private handleReplaceField(fieldCid: string, newField: ClientSchemaField): void {
    const fieldIndex = this.state.fields.findIndex((f) => f.cid === fieldCid);
    const newFields = reactUpdate(this.state.fields, {
      $splice: [[fieldIndex, 1, newField]],
    });
    if (!this.props.isBeingCreated && !this.verifyFields(newFields)) {
      // Don't run the verification check if new fields are being created, otherwise the check will
      // be run every time the value of the field changes. Instead, for that case, handle verification
      // in the parent.
      this.setState(mapPropsToState(this.props));
      return;
    }
    const editingFieldCidIndex = this.state.editingFieldCids.findIndex((cid) => cid === fieldCid);
    const newEditingFieldCids =
      !this.props.isBeingCreated && editingFieldCidIndex > -1
        ? reactUpdate(this.state.editingFieldCids, {
            $splice: [[editingFieldCidIndex, 1]],
          })
        : this.state.editingFieldCids;
    this.setState({
      editingFieldCids: newEditingFieldCids,
      fields: newFields,
    });
    if (this.props.onFieldsChange) {
      this.props.onFieldsChange(newFields);
    }
  }

  private handleFieldMigrationCreation = async (
    fieldApiId: string,
    destinationFieldConfig?: ClientSchemaField,
    destinationFieldId?: string,
    locale?: string
  ): Promise<void> => {
    await withLoadingScreen<void>(
      (async (): Promise<void> => {
        await SchemaFieldMigrationDAO.createFieldMigration(
          fieldApiId,
          destinationFieldConfig ? omit(destinationFieldConfig, 'apiId', 'dbId', 'id', 'cid') : null,
          destinationFieldId || null,
          locale || null
        );
        await this.props.refetchSchemaAfterUpdate?.();
        notifications.success('Field migration created successfully.');
        if (destinationFieldConfig) {
          const editingFieldCidIndex = this.state.editingFieldCids.findIndex(
            (cid) => cid === destinationFieldConfig.cid
          );
          const newEditingFieldCids: Array<string> = reactUpdate(this.state.editingFieldCids, {
            $splice: [[editingFieldCidIndex, 1]],
          });
          this.setState({editingFieldCids: newEditingFieldCids});
        }
      })()
    );
  };

  private handlePopulateFromAnotherField = (destinationField: SchemaFieldConstants.SchemaField): void => {
    runModal(PopulateFromAnotherFieldModal, {
      destinationField,
      schema: this.props.schema!,
      schemaNumItems: this.props.schemaNumItems!,
      shouldShowIsParentLink: this.props.shouldShowIsParentLink ? this.props.shouldShowIsParentLink : false,
      onRequestSubmit: async (settings: FieldToFieldMigrationSettings) => {
        await this.handleFieldMigrationCreation(
          settings.sourceFieldId,
          undefined,
          destinationField.apiId,
          settings.locale
        );
      },
      onRequestClose: () => {},
      shouldShowIsIntegration: this.props.shouldShowIsIntegration
        ? this.props.shouldShowIsIntegration
        : false,
      schemaFieldSelectors: this.props.schemaFieldSelectors,
    });
  };

  private handleMoveField = (newOrderedCids: Array<string>): void => {
    const fieldsByCid = _.indexBy(this.state.fields, 'cid');
    let orderedHiddenCids: Array<string> = [];
    if (newOrderedCids.includes(SHOW_ARCHIVED_ROW_ID)) {
      newOrderedCids.splice(newOrderedCids.indexOf(SHOW_ARCHIVED_ROW_ID), 1);
      orderedHiddenCids = this.state.fields
        .map((field) => field.cid)
        .filter((cid) => !newOrderedCids.includes(cid));
    }
    this.setState({
      fields: [...newOrderedCids, ...orderedHiddenCids].map((cid) => fieldsByCid[cid]),
    });
  };

  private handleMoveFieldComplete = (fieldCid: string): void => {
    const fieldIndex = this.state.fields.findIndex((f) => f.cid === fieldCid);
    const field = this.state.fields[fieldIndex];
    const originalFieldIndex = this.props.fields.findIndex((f) => f.id === field.id);
    if (originalFieldIndex != null && fieldIndex !== originalFieldIndex) {
      if (this.props.onFieldsChange) {
        this.props.onFieldsChange(this.state.fields);
      }
    }
  };

  private handleRemoveField(fieldCid: string): void {
    const fieldIndex = this.state.fields.findIndex((f) => f.cid === fieldCid);
    const field = this.state.fields[fieldIndex];
    const originalField = this.props.fields.findIndex((f) => f.id === field.id);
    if (field.id == null) {
      const newFields = reactUpdate(this.state.fields, {
        $splice: [[fieldIndex, 1]],
      });
      this.setState({fields: newFields});
      if (this.props.isBeingCreated && this.props.onFieldsChange) {
        this.props.onFieldsChange(newFields);
      }
    } else if (this.props.handleDeleteConfirmation && originalField != null) {
      this.props.handleDeleteConfirmation((deleteParameter: string) => {
        this.handleRemoveFieldComplete(fieldCid, deleteParameter);
      });
    } else {
      this.handleRemoveFieldComplete(fieldCid);
    }
  }

  private handleRemoveFieldComplete(fieldCid: string, deleteParameter?: string): void {
    const fieldIndex = this.state.fields.findIndex((f) => f.cid === fieldCid);
    const deleteOldSchemaTags = deleteParameter === CONFIRM_DELETE_ALL;
    const newFields = reactUpdate(this.state.fields, {
      $splice: [[fieldIndex, 1]],
    });
    const editingFieldCidIndex = this.state.editingFieldCids.findIndex((cid) => cid === fieldCid);
    const newEditingFieldCids =
      editingFieldCidIndex > -1
        ? reactUpdate(this.state.editingFieldCids, {
            $splice: [[editingFieldCidIndex, 1]],
          })
        : this.state.editingFieldCids;

    this.setState({
      editingFieldCids: newEditingFieldCids,
      fields: newFields,
    });

    if (this.props.onFieldsChange) {
      this.props.onFieldsChange(newFields, {deleteOldSchemaTags});
    }

    if (this.props.onFieldSave) {
      this.props.onFieldSave(this.state.fields[fieldIndex], SaveAction.REMOVE);
    }
  }

  private handleArchiveField(fieldCid: string): void {
    const fieldIndex = this.state.fields.findIndex((f) => f.cid === fieldCid);
    const field = this.state.fields[fieldIndex];
    const fieldForArchiveModal = {
      ...field,
      // TODO(BNCH-966): Remove.
      id: field.apiId!,
    };
    const archiveWarnings = [...(this.props.archiveWarnings ?? [])];
    if (field.relatedBenchlingApps != null && field.relatedBenchlingApps.length > 0) {
      archiveWarnings.push(
        <RelatedBenchlingAppsArchiveWarning
          itemName={field.name}
          itemType="field"
          relatedBenchlingApps={field.relatedBenchlingApps}
        />
      );
    }

    ArchiveModal.open({
      items: [fieldForArchiveModal],
      onArchive: (itemIds: [string], purpose: string) => {
        if (archiveWarnings.length) {
          confirm(`Confirm archiving "${field.name}"?`, archiveWarnings, ButtonStyle.NEGATIVE, {
            enableRedesign: true,
            acceptText: 'Archive',
            onSubmit: () => {
              this.handleArchiveFieldConfirm(itemIds, purpose);
            },
          });
        } else {
          this.handleArchiveFieldConfirm(itemIds, purpose);
        }
      },
    });
  }

  private async handleArchiveFieldConfirm(itemIds: [string], purpose: string): Promise<void> {
    const [fieldId] = itemIds;
    const fieldIndex = this.state.fields.findIndex((f) => f.apiId === fieldId);
    let field = this.state.fields[fieldIndex];
    if (field.isSnapshot) {
      const convertSnapshotFieldResponse = await withLoadingScreen<ConvertSnapshotFieldResponse>(
        convertSnapshotField({
          snapshotFieldId: fieldId,
        }),
        'Updating...'
      );
      field = {
        ...convertSnapshotFieldResponse.schemaField,
        cid: field.cid,
      };
    }
    const newFieldData = {...field, archiveRecord: {purpose}};

    this.handleReplaceField(field.cid, newFieldData);

    if (this.props.onFieldSave) {
      this.props.onFieldSave(newFieldData, SaveAction.ARCHIVE);
    }
  }

  private handleUnarchiveField(fieldCid: string): void {
    const field = this.state.fields.find((f) => f.cid === fieldCid)!;
    const newFieldData = {...field, archiveRecord: null};

    this.handleReplaceField(fieldCid, newFieldData);

    if (this.props.onFieldSave) {
      this.props.onFieldSave(newFieldData, SaveAction.UNARCHIVE);
    }
  }

  private getVisibleFieldRowDatas(): Array<ItemData<ItemProps>> {
    const [archivedFields, notArchivedFields]: [Array<ClientSchemaField>, Array<ClientSchemaField>] =
      _.partition(this.state.fields, (field) => isArchived({...field, id: field.apiId ?? ''})) as [
        Array<ClientSchemaField>,
        Array<ClientSchemaField>,
      ];
    const visibleFieldsInOrder: Array<ClientSchemaField> = [
      ...notArchivedFields,
      ...(this.state.isShowingArchived ? archivedFields : []),
    ];
    const isActiveFieldMigration =
      (BenchlingConfig.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION ||
        BenchlingConfig.SCHEMAS_ENABLE_MANUAL_SCHEMA_FIELD_MIGRATION) &&
      this.props.activeMigration != null;

    const isReviewableForFieldMigration = (field: ClientSchemaField): boolean =>
      isActiveFieldMigration &&
      this.props.activeMigration?.sourceField?.apiId === field.apiId &&
      this.props.activeMigration?.status === FieldDataMigrationStatus.SUCCEEDED;

    const getMigratingFromFieldName = (field: ClientSchemaField): string | null => {
      if (
        isActiveFieldMigration &&
        this.props.activeMigration?.destinationField?.apiId === field.apiId &&
        this.props.activeMigration?.sourceField
      ) {
        return this.props.activeMigration.sourceField.name;
      }
      return null;
    };

    const getMigratingToFieldName = (field: ClientSchemaField): string | null => {
      if (
        isActiveFieldMigration &&
        this.props.activeMigration?.sourceField?.apiId === field.apiId &&
        this.props.activeMigration?.destinationField
      ) {
        return this.props.activeMigration.destinationField.name;
      }
      return null;
    };

    const isFieldPartOfActiveMigration = (field: ClientSchemaField): boolean =>
      isActiveFieldMigration &&
      (this.props.activeMigration?.sourceField?.apiId === field.apiId ||
        this.props.activeMigration?.destinationField?.apiId === field.apiId);

    const reviewMigration = (): void => {
      if (!isActiveFieldMigration || this.props.schemaId == null) {
        return;
      }
      // TODO (BNCH-86126): query for sourceField.name / warehouseName instead of performing this lookup
      const sourceField = notArchivedFields.find(
        (field) => field.apiId === this.props.activeMigration?.sourceField?.apiId
      );
      if (sourceField == null) {
        return;
      }
      const destinationField = archivedFields.find(
        (field) => field.apiId === this.props.activeMigration?.destinationField?.apiId
      );
      if (destinationField == null) {
        notifications.error(
          'No destination field found for field migration. Please contact Benchling Support.'
        );
        return;
      }
      runModal(FieldMigrationReviewModal, {
        onRequestRollback: async () => await this.props.refetchSchemaAfterUpdate?.(),
        onRequestPublish: async () => await this.props.refetchSchemaAfterUpdate?.(),
        onRequestClose: () => {},
        schemaId: this.props.schemaId,
        migration: this.props.activeMigration,
        originalFieldAttributes: {
          displayName: sourceField.displayName,
          warehouseName: sourceField.systemName,
        },
        destinationFieldAttributes: {
          displayName: destinationField.displayName,
          warehouseName: destinationField.systemName,
        },
      });
    };

    return visibleFieldsInOrder.map((field) => {
      const isFieldsetConstrained = field.appliedFieldset != null;
      const disabled =
        this.props.isDisabled ||
        isFieldPartOfActiveMigration(field) ||
        (isFieldsetConstrained && !BenchlingConfig.SCHEMAS_ENABLE_INTERFACE_FIELD_TOOLTIP_EDITING);
      const disableDrag =
        this.props.isDisabled ||
        isFieldPartOfActiveMigration(field) ||
        (!isClientSchemaField(field) && isSchemaFieldArchived(field));
      const isNewField = field.id == null;
      const isInEditMode =
        this.state.editingFieldCids.some((cid) => cid === field.cid) ||
        (isNewField && field.appliedFieldset == null);
      const isFieldMigrationAvailable = Boolean(
        this.props.isFieldMigrationEnabled &&
          this.props.schemaNumItems &&
          this.props.schemaNumItems >= BenchlingConfig.SCHEMAS_NUM_SCHEMATIZED_ITEMS_SFM_THRESHOLD
      );
      const shouldDisableAllControlsExceptTooltip =
        isFieldsetConstrained && BenchlingConfig.SCHEMAS_ENABLE_INTERFACE_FIELD_TOOLTIP_EDITING;
      const isFieldsetConstrainedMessage = 'Cannot edit field constrained by fieldset';
      const disableControlsWithTooltips = shouldDisableAllControlsExceptTooltip
        ? {
            [DisablableControl.ARCHIVE_BUTTON]: isFieldsetConstrainedMessage,
            [DisablableControl.DELETE_BUTTON]: isFieldsetConstrainedMessage,
            [DisablableControl.DISPLAY_NAME_INPUT]: isFieldsetConstrainedMessage,
            [DisablableControl.FIELD_DEFINITION_DROPDOWN]: isFieldsetConstrainedMessage,
            [DisablableControl.IS_MULTI_CHECKBOX]: isFieldsetConstrainedMessage,
            [DisablableControl.IS_REQUIRED_CHECKBOX]: isFieldsetConstrainedMessage,
            [DisablableControl.WAREHOUSE_NAME_INPUT]: isFieldsetConstrainedMessage,
          }
        : undefined;

      const shouldEnableLink = shouldEnableSchemaLinkForField(field, {
        tagSchemasInOrg: this.props.tagSchemas,
        availableSchemaFieldSelectors: this.props.schemaFieldSelectors,
        ownerId: this.props.ownerId,
        registryId: this.props.registryId ?? undefined,
      });

      return {
        itemClass: this.props.itemClass,
        id: field.cid,
        itemProps: {
          id: field.cid,
          disabled,
          disableDrag,
          disableControlsWithTooltips,
          initialField: field,
          isInEditMode,
          isSchemaOrInterfaceBeingCreated: Boolean(this.props.isBeingCreated),
          fileType: this.props.fileType,
          itemType: this.props.itemType,
          sequenceType: this.props.sequenceType,
          onCancel: (fieldCid: string) => {
            this.handleCancelEditField(fieldCid);
          },
          onEdit: (fieldCid: string) => {
            this.handleEditField(fieldCid);
          },
          tagSchemas: this.props.tagSchemas,
          batchSchemas: this.props.batchSchemas,
          storableSchemas: this.props.storableSchemas,
          assaySchemas: this.props.assaySchemas,
          schemaInterfaces: this.props.schemaInterfaces,
          refetchSchemaAfterUpdate: this.props.refetchSchemaAfterUpdate,
          requestArchive: (fieldCid: string) => {
            this.handleArchiveField(fieldCid);
          },
          requestChange: (fieldCid: string, newFieldData: Partial<ClientSchemaField>) => {
            this.handleChangeField(fieldCid, newFieldData);
          },
          requestRemove: (fieldCid: string) => {
            this.handleRemoveField(fieldCid);
          },
          requestUnarchive: (fieldCid: string) => {
            this.handleUnarchiveField(fieldCid);
          },
          requestFieldMigrationCreation: async (
            fieldApiId: string,
            destinationField: ClientSchemaField,
            locale?: string
          ) => {
            await this.handleFieldMigrationCreation(fieldApiId, destinationField, undefined, locale);
          },
          requestPopulateFromAnotherField: (destinationField: SchemaFieldConstants.SchemaField) => {
            this.handlePopulateFromAnotherField(destinationField);
          },
          schemaFieldSelectors: this.props.schemaFieldSelectors,
          shouldShowIsParentLink: this.props.shouldShowIsParentLink,
          shouldShowIsRequired: this.props.shouldShowIsRequired,
          shouldShowMultiSelect: this.props.shouldShowMultiSelect,
          shouldShowValidations: this.props.shouldShowValidations,
          shouldShowTooltipText: this.props.shouldShowTooltipText,
          shouldShowIsIntegration: this.props.shouldShowIsIntegration,
          batchLinkProps: this.props.batchLinkProps,
          isFieldBeingCreated: field.dbId == null,
          registryId: shouldEnableLink ? this.props.registryId : undefined,
          ownerId: this.props.ownerId,
          ownerUrlId: shouldEnableLink ? this.props.ownerUrlId : '',
          schema: this.props.schema,
          schemaType: this.props.schemaType,
          schemaId: this.props.schemaId,
          schemaHasData: this.props.schemaHasData,
          schemaNumItems: this.props.schemaNumItems,
          schemaFieldDependentsSummaries:
            this.props.schemaFieldDependentsSummariesById != null && field.apiId != null
              ? this.props.schemaFieldDependentsSummariesById?.[field.apiId]
              : null,
          submissionWarnings: this.props.submissionWarnings != null ? this.props.submissionWarnings : [],
          migratingFromFieldName: getMigratingFromFieldName(field),
          migratingToFieldName: getMigratingToFieldName(field),
          isFieldMigrationAvailable,
          ...(this.props.activeMigration == null
            ? {}
            : {
                fieldMigrationReviewProps: {
                  isReviewableForFieldMigration: isReviewableForFieldMigration(field),
                  reviewMigration,
                },
              }),
          fieldNameRenderingProps: this.props.fieldNameRenderingProps,
          compactMode: this.props.compactMode,
        },
      };
    });
  }

  private getShowArchivedRowData(): ItemData<ReorderableSimpleTableRowProps> | null {
    const archivedFieldsCount = this.state.fields.filter((field) =>
      isArchived({...field, id: field.apiId ?? ''})
    ).length;
    if (this.state.isShowingArchived || archivedFieldsCount === 0) {
      return null;
    }
    return {
      id: SHOW_ARCHIVED_ROW_ID,
      itemClass: ReorderableSimpleTableRow,
      itemProps: {
        isDraggingDisabled: true,
        row: {
          id: SHOW_ARCHIVED_ROW_ID,
          cells: [
            {
              key: SHOW_ARCHIVED_ROW_ID,
              contents: (
                <div>
                  <a
                    href=""
                    onClick={(e) => {
                      e.preventDefault();
                      this.setState({isShowingArchived: true});
                    }}
                    {...getTestProps({component: 'FieldTable', element: 'showArchived'})}
                  >
                    Show archived fields ({archivedFieldsCount})
                  </a>
                </div>
              ),
            },
          ],
        },
      },
    };
  }

  override render(): JSX.Element {
    if (this.props.onFieldSave && this.props.onFieldsChange) {
      throw new Error('FieldTable cannot have both onFieldSave and onFieldsChange props.');
    }

    if (!this.props.onFieldSave && !this.props.onFieldsChange) {
      throw new Error('FieldTable must have either onFieldSave or onFieldsChange prop.');
    }

    return (
      <div
        className="simpleDivTable u-small-spacing-bottom u-small-spacing-top"
        {...getTestProps(
          this.props.dataTestAttrs ? this.props.dataTestAttrs : {component: 'FieldTable', element: 'root'}
        )}
      >
        <FieldTableHeader
          isDisabled={this.props.isDisabled}
          onAddField={
            this.shouldDisableAddField()
              ? null
              : () => {
                  analytics.trackEvent(SCHEMAS_ANALYTICS_ACTIONS.ADD_FIELD, {
                    fieldCount: this.state.fields.length,
                    buttonLocation: 'field-table-header',
                  });
                  this.handleAddField();
                }
          }
          shouldShowDefinition={true}
          shouldShowIsParentLink={this.props.shouldShowIsParentLink}
          shouldShowIsRequired={this.props.shouldShowIsRequired}
          shouldShowMultiSelect={this.props.shouldShowMultiSelect}
          shouldShowValidations={this.props.shouldShowValidations}
          shouldShowIsIntegration={this.props.shouldShowIsIntegration}
          shouldShowWarehouseName={shouldShowWarehouseName()}
          subheader={this.props.subheader}
          title={this.props.title ?? `${capitalize(this.props.schemaType)} fields`}
          helpText={this.props.titleTooltip}
          compactMode={this.props.compactMode}
        />
        {(() => {
          if (!this.props.schemaFieldSelectors) {
            return (
              <div className="u-spacing-top u-spacing-bottom u-justify-content-center">
                <HorizontalThrobber />
              </div>
            );
          } else if (this.state.fields.length === 0) {
            return (
              <div className="simpleDivTable-row">
                <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">
                  {`This ${this.props.nonSchemaNameOverride ?? 'schema'} does not have any ${this.props.schemaType} fields`}
                </div>
              </div>
            );
          }
          return (
            <ReorderableList<ItemProps | ReorderableSimpleTableRowProps>
              itemDatas={_.compact([...this.getVisibleFieldRowDatas(), this.getShowArchivedRowData()])}
              onMoveItem={this.handleMoveField} // called with (fieldCid, newIndex)
              onMoveItemEnd={this.handleMoveFieldComplete} // called with (fieldCid)
            />
          );
        })()}
        {!this.shouldDisableAddField() && (
          <div className="simpleDivTable-row">
            <div className="simpleDivTable-cell">
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                onClick={() => {
                  analytics.trackEvent(SCHEMAS_ANALYTICS_ACTIONS.ADD_FIELD, {
                    fieldCount: this.state.fields.length,
                    buttonLocation: 'field-table-footer',
                  });
                  this.handleAddField();
                }}
                disabled={this.shouldDisableAddField()}
                size={ButtonSize.SMALL}
              >
                Add field
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

interface FieldTableLoadingProps {
  dataTestAttrs?: DataTestAttrs;
  schemaType: string;
}

/**
 * This component can be used to show an "in-flight" field table while you load data. Note that
 * despite its name, this component doesn't actually do any loading; it merely indicates that
 * a parent component is still loading the relevant data.
 *
 * It also does not automatically revert to the actual FieldTable when loading is complete. You
 * are responsible for that too.
 */
export function FieldTableLoading(props: FieldTableLoadingProps): JSX.Element {
  return (
    <div
      className="simpleDivTable"
      {...getTestProps(
        props.dataTestAttrs ? props.dataTestAttrs : {component: 'FieldTableLoading', element: 'root'}
      )}
    >
      <FieldTableHeader
        isDisabled={true}
        onAddField={null}
        shouldShowDefinition={false}
        shouldShowIsParentLink={false}
        shouldShowIsRequired={false}
        shouldShowMultiSelect={false}
        shouldShowValidations={false}
        shouldShowIsIntegration={false}
        shouldShowWarehouseName={false}
        title={`${capitalize(props.schemaType)} fields`}
      />
      <div className="u-spacing-top u-spacing-bottom u-justify-content-center">
        <HorizontalThrobber />
      </div>
    </div>
  );
}

export const FIELD_TABLE_QUERY: DocumentNode = gql`
  query FieldTableQuery($organizationIdFilter: StrictString!, $searchInput: TagSchemaListingSearchInput)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    schemaInterfaces: allSchemaInterfaces(organizationIdFilter: $organizationIdFilter) {
      id
      name
      ...HasArchiveRecordGraphQL
    }
    tagSchemasListing(searchInput: $searchInput) {
      tagSchemas {
        ...TagSchemaSummaryGraphQL
      }
    }

    schemaFieldSelectors {
      ... on SchemaFieldSelectorNode {
        ...SchemaFieldSelectorGraphQL
        registry {
          id
          organization {
            id
          }
        }
      }
    }
  }

  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
  ${getFragmentWrapper('TagSchemaSummary').fragment}
`;

export const FIELD_TABLE_SCHEMA_QUERY: DocumentNode = gql`
  # schema.allFieldsNodes.relatedBenchlingApps does not query id
  # eslint-disable benchlint/id-field-required
  query FieldTableSchemaQuery($id: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    schema: schema(id: $id) {
      ... on SchemaInterface {
        id
        allFieldsNodes {
          id
          apiId
          relatedBenchlingApps {
            name
            url
          }
          appliedFieldset {
            name
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function addSelectorToFieldTableQueryCache(selector: FieldTableQuerySelector): void {
  const organizationId = selector.registry.organization.id;
  const queryVariables = getFieldTableQueryVariables(organizationId, selector.registry.id);

  const fieldTableQuery = readQuerySafe<FieldTableQuery, FieldTableQueryVariables>(
    {query: FIELD_TABLE_QUERY},
    {variables: queryVariables}
  );

  if (!fieldTableQuery) {
    return;
  }

  const newData = {
    ...fieldTableQuery,
    schemaFieldSelectors: naturalSortByKey([...fieldTableQuery.schemaFieldSelectors, selector], 'name'),
  };
  writeQuery<FieldTableQuery, FieldTableQueryVariables>({query: FIELD_TABLE_QUERY}, newData, queryVariables);
}

export function addTagSchemaToFieldTableQueryCache(
  tagSchema: TagSchemaGQL,
  registryId: string,
  organizationId: string
): void {
  const queryVariables = getFieldTableQueryVariables(organizationId, registryId);
  const fieldTableQuery = readQuerySafe<FieldTableQuery, FieldTableQueryVariables>(
    {query: FIELD_TABLE_QUERY},
    {variables: queryVariables}
  );

  if (!fieldTableQuery) {
    return;
  }

  const newData = {
    ...fieldTableQuery,
    tagSchemasListing: {
      ...fieldTableQuery.tagSchemasListing,
      tagSchemas: naturalSortByKey([...fieldTableQuery.tagSchemasListing.tagSchemas, tagSchema], 'name'),
    },
  };
  writeQuery<FieldTableQuery, FieldTableQueryVariables>({query: FIELD_TABLE_QUERY}, newData, queryVariables);
}

function _mergeGraphQLSchemaFields(
  fields: Array<ClientSchemaField | SchemaFieldConstants.SchemaField>,
  graphqlSchema: FieldTableSchemaQuerySchema
): Array<ClientSchemaField | SchemaFieldConstants.SchemaField> {
  if (isInaccessible(graphqlSchema)) {
    return fields;
  } else {
    return fields.map((propField) => ({
      ...propField,
      relatedBenchlingApps:
        graphqlSchema.allFieldsNodes.find(
          (graphQLField) => graphQLField.apiId === propField.apiId || graphQLField.id === propField.id
        )?.relatedBenchlingApps || [],
      appliedFieldset:
        graphqlSchema.allFieldsNodes.find(
          (graphQLField) => graphQLField.apiId === propField.apiId || graphQLField.id === propField.id
        )?.appliedFieldset || null,
    }));
  }
}

export function getFieldTableQueryVariables(ownerId: string, registryId?: string): FieldTableQueryVariables {
  const searchInput: TagSchemaListingSearchInput = {includeFilteredSsis: true};
  if (registryId) {
    searchInput.registryId = registryId;
  } else if (ownerId) {
    searchInput.organizationId = ownerId;
  }
  return {
    organizationIdFilter: ownerId,
    searchInput,
  };
}

function QueryingFieldTable(props: BaseFieldTableProps): JSX.Element {
  let mergedFields;
  const fieldTableSchemaQuery = useSkippableBenchlingQuery<
    FieldTableSchemaQuery,
    FieldTableSchemaQueryVariables
  >(FIELD_TABLE_SCHEMA_QUERY, {
    variables: {id: props.schemaId!},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
    skip: !props.schemaId || !props.schema,
  });
  if (fieldTableSchemaQuery.type === QueryResultType.SUCCESS && props.schema != null) {
    mergedFields = _mergeGraphQLSchemaFields(props.fields, fieldTableSchemaQuery.data.schema);
  } else {
    mergedFields = props.fields;
  }

  const propsWithAdditionalInfo = {
    ...props,
    fields: mergedFields,
  };
  const queryVariables = getFieldTableQueryVariables(props.ownerId, props.registryId);
  return (
    <BenchlingQuery<FieldTableQuery, FieldTableQueryVariables>
      query={FIELD_TABLE_QUERY}
      variables={queryVariables}
      renderLoading={() => <FieldTableLoading schemaType={props.schemaType} />}
    >
      {(data) => {
        const existingFieldsWithSelectors = props.fields.filter(
          (field) => field.fieldType === SchemaFieldConstants.FieldType.SELECTOR
        );
        const schemaFieldSelectors = data.schemaFieldSelectors.filter((selector) => {
          const matchesOwner = selector.registry.organization.id === props.ownerId;
          const matchesRegistry = props.registryId == null || selector.registry.id === props.registryId;
          // Some customers have schemas with selectors fields from other organizations. We want to make
          // sure these still show up in the field table, but that new fields only use selectors from
          // their organization.
          const matchesExistingField = existingFieldsWithSelectors.some(
            (s) => s.schemaFieldSelectorId === selector.id
          );

          return (matchesOwner && matchesRegistry) || matchesExistingField;
        });

        const tagSchemas = data.tagSchemasListing.tagSchemas.filter((ts) =>
          props.tagSchemasFilter ? props.tagSchemasFilter(ts, props) : true
        );

        return (
          <FieldTable
            {...propsWithAdditionalInfo}
            schemaInterfaces={data.schemaInterfaces}
            schemaFieldSelectors={schemaFieldSelectors}
            tagSchemas={tagSchemas}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export default QueryingFieldTable;
